import { useEffect, useRef, useState } from 'react'
import type { IScannerControls } from '@zxing/browser'

export function ProductionScanner({ onRead, onClose }: Readonly<{
  onRead: (code: string) => void
  onClose: () => void
}>) {
  const video = useRef<HTMLVideoElement>(null)
  const readCallback = useRef(onRead)
  const [error, setError] = useState('')
  useEffect(() => { readCallback.current = onRead }, [onRead])

  useEffect(() => {
    let disposed = false
    let controls: IScannerControls | undefined
    const element = video.current
    if (!element) return
    const start = async () => {
      try {
        const [{ BrowserMultiFormatReader }, { ChecksumException, FormatException, NotFoundException }] =
          await Promise.all([import('@zxing/browser'), import('@zxing/library')])
        if (disposed) return
        const reader = new BrowserMultiFormatReader()
        controls = await reader.decodeFromConstraints(
          { audio: false, video: { facingMode: { ideal: 'environment' } } },
          element,
          (result, failure, scanner) => {
            if (disposed) return
            if (result) {
              disposed = true
              scanner.stop()
              readCallback.current(result.getText())
            } else if (failure && !(failure instanceof NotFoundException) &&
              !(failure instanceof ChecksumException) && !(failure instanceof FormatException)) {
              setError('Não foi possível ler o código. Feche e use o leitor de teclado ou entrada manual.')
              scanner.stop()
            }
          },
        )
        if (disposed) controls.stop()
      } catch {
        if (!disposed) setError('Câmara indisponível. Autorize o acesso e use HTTPS (ou localhost). Pode usar leitor de teclado ou entrada manual.')
      }
    }
    void start()
    return () => {
      disposed = true
      controls?.stop()
      if (element.srcObject instanceof MediaStream) {
        element.srcObject.getTracks().forEach(track => track.stop())
        element.srcObject = null
      }
    }
  }, [])

  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    role="dialog" aria-modal="true" aria-label="Ler QR / código de barras"
    onKeyDown={event => { if (event.key === 'Escape') onClose() }}>
    <div className="w-full max-w-lg space-y-3 rounded bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">Ler QR / código de barras</h3>
        <button type="button" className="btn btn-secondary" autoFocus onClick={onClose}>Fechar câmara</button>
      </div>
      <video ref={video} className="max-h-96 w-full rounded" muted playsInline />
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <p className="text-sm">Aponte ao código. A leitura será validada no servidor antes de adicionar o consumo.</p>
    </div>
  </div>
}
